import * as types from "../../constants";
import { 
    deletePatient, 
    fetchPatient,
    getAllPatientsInvestigation, 
    saveListPatients, 
    updatePatientPersonalData
} from "../../db";
import {
    addPatient as addPatientService, 
    updatePersonalDataPatientService,
    getPatients,
    deletePatientService,
    getPatientEvents,
    getPatientByUuid,
} from "../../services";
import {
    getLastSyncEventId,
    setLastSyncEventId,
    getSyncState
} from "../../utils";
import { PAGINATION_CONFIG } from "../../config/pagination";
import { addBreadcrumb, flush } from "../../utils/diagnostics/breadcrumbs";
import { applyPatientSyncPatches, upsertPatientInList } from "../../utils/patientListUtils";

// SHER-360: fetch one page, retrying transient transport errors (no response
// and no status — e.g. a QUIC/HTTP-3 stream cut where the server answered 200
// but the browser never got the body). Auth/API errors (401, 403, 5xx…) are
// rethrown immediately so the existing error paths keep their semantics.
async function getPatientsPageWithRetry(uuidInvestigation, params, retryDelaysMs, pageNumber) {
    let lastError;
    for (let attempt = 0; attempt <= retryDelaysMs.length; attempt++) {
        try {
            return await getPatients(uuidInvestigation, 0, params);
        } catch (error) {
            const isTransient = !error?.status && !error?.response;
            if (!isTransient) {
                throw error;
            }
            lastError = error;
            if (attempt < retryDelaysMs.length) {
                const delayMs = retryDelaysMs[attempt];
                console.warn(`🔁 Transient network error on page ${pageNumber}, retry ${attempt + 1}/${retryDelaysMs.length} in ${delayMs}ms`);
                addBreadcrumb('state', 'patients.page.retry', {
                    pageNumber,
                    offset: params.offset,
                    attempt: attempt + 1,
                    delayMs,
                    errorMessage: error?.message,
                });
                if (delayMs > 0) {
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                }
            }
        }
    }
    throw lastError;
}

function memoryMB() {
    try {
        const m = typeof performance !== 'undefined' ? performance.memory : undefined;
        if (m && typeof m.usedJSHeapSize === 'number') {
            return Math.round(m.usedJSHeapSize / (1024 * 1024));
        }
    } catch (_) { /* ignore */ }
    return undefined;
}

export function savePatientAction(investigation, patientData) {
  //Añado un uuid por si falla la conexión tener una referencia de este paciente
  //Para también usarlo en el reenvio.
  patientData.uuid = Math.random().toString(36).substring(2); 
  return async (dispatch, getState) => {
    dispatch({ type: types.SAVE_PATIENT_LOADING });

    return addPatientService(investigation.uuid, patientData)
        .then(async (response) => {
            await saveListPatients([response.patient], investigation);
            const stored = await fetchPatient(response.patient.uuid);
            const currentList = getState().patients.data?.[investigation.uuid] || [];
            const updatedListPatients = upsertPatientInList(currentList, stored);
            investigation.patientsPersonalData = updatedListPatients;
            dispatch({
                type: types.SAVE_PATIENT_SUCCESS,
                patient: {...response.patient},
                investigation:investigation
            });
            return response.patient;
      })
      .catch((error) => {
        if(!error.status && !error.response){
          const offlinePost = patientData;          
          dispatch({
            type: types.SAVE_PATIENT_OFFLINE,
            patient: {...offlinePost},
            investigation:investigation
          });
        }
        else{
          dispatch({ type: types.SAVE_PATIENT_ERROR });
          throw error;
        }
        
       
      });
  };
}

export function deletePatientAction(uuidInvestigation, uuidPatient, reason) {
    return async (dispatch) => {
        // SHER-724: the purge used to be dispatched up front, optimistically. That was
        // survivable while the delete could not fail for a reason the user could correct,
        // but it cannot survive a rejected reason: the patient left the store before the
        // request went out, so a 400 left the page rendering "this patient does not exist"
        // for a patient the server still holds. Purge only once the server has confirmed.
        return deletePatientService(uuidInvestigation, uuidPatient, reason).then(async (response) => {
            dispatch({
                type: types.DELETE_PATIENT,
                payload: { uuidInvestigation, uuidPatient }
            });
            await deletePatient(uuidPatient);
        });
    }
}

export function fetchPatientsAction(uuidInvestigation, silent = false, options = {}) {
    return async (dispatch, getState) => {
        // A bulk download can be triggered concurrently (selectInvestigation and
        // usePatients both fire it on a cold start). Running two 51k-patient
        // downloads doubles bandwidth, and the first FETCH_NEW_PATIENTS_SUCCESS
        // would drop the blocking overlay while the second is still mid-flight.
        if (getState().patients.downloading) {
            console.log('⏭️ Bulk patient download already in progress, skipping duplicate fetch');
            return;
        }

        if(!silent){
            dispatch({ type: types.UPDATING_PATIENTS_LOADING });
        } else {
            dispatch({ type: types.SAVE_PATIENT_LOADING });
        }
        
        const state = getState();
        const investigations = state.investigations;
        const investigation = investigations.data.find(inv => inv.uuid === uuidInvestigation);
        if (!investigation) {
            console.error('❌ Investigation not found:', uuidInvestigation);
            return;
        }

        // Dispatch after the investigation guard so an early return can't leave
        // the blocking overlay stuck on screen. Silent (background) downloads
        // must not block the UI.
        if(!silent){
            dispatch({ type: types.PATIENTS_DOWNLOAD_START });
        }

        // Event-based sync: full sync is always paginated snapshot (no timestamp-based API)
        const pageSize = options.pageSize || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
        const pageRetryDelaysMs = options.pageRetryDelaysMs || PAGINATION_CONFIG.PAGE_RETRY_DELAYS_MS;
        const currentCallTimestamp = new Date().getTime().toString();
        let offset = 0;
        let allPatients = [];
        let hasMore = true;
        let lastResponse = null; // Track last response for latestEventId
        
        try {
            console.time('⏱️ Total patient fetch with pagination');
            console.log(`🚀 Starting pagination: ${pageSize} patients per page, max ${PAGINATION_CONFIG.MAX_TOTAL_PATIENTS}`);
            
            // Fetch patients in batches until no more data
            while (hasMore) {
                // Check if investigation is still current (user might have switched)
                const currentState = getState();
                const currentInvestigationUuid = currentState.investigations.currentInvestigation?.uuid;
                
                if (currentInvestigationUuid !== uuidInvestigation) {
                    console.log('🔄 Investigation changed, stopping pagination');
                    break;
                }
                
                const pageNumber = Math.floor(offset / pageSize) + 1;
                console.log(`📦 Fetching page ${pageNumber} (offset: ${offset}, size: ${pageSize})`);
                const pageStartMs = Date.now();
                addBreadcrumb('state', 'patients.page.start', {
                    pageNumber,
                    offset,
                    pageSize,
                    memoryUsedMB: memoryMB(),
                });

                const response = await getPatientsPageWithRetry(
                    investigation.uuid,
                    { limit: pageSize, offset: offset },
                    pageRetryDelaysMs,
                    pageNumber
                );

                lastResponse = response; // Keep track of last response

                addBreadcrumb('state', 'patients.page.end', {
                    pageNumber,
                    offset,
                    pageSize,
                    returnedCount: response?.patients?.length ?? 0,
                    total: response?.pagination?.total,
                    hasMore: response?.pagination?.hasMore,
                    durationMs: Date.now() - pageStartMs,
                    memoryUsedMB: memoryMB(),
                });

                // Safety check: backend returns hasMore=true but empty array
                if (response.patients.length === 0 && response.pagination?.hasMore) {
                    console.warn('⚠️ Backend returned hasMore=true with no patients, stopping pagination');
                    addBreadcrumb('exception', 'patients.empty-with-hasMore', {
                        pageNumber,
                        offset,
                        pageSize,
                        total: response?.pagination?.total,
                    });
                    flush('pagination-failed');
                    hasMore = false;
                    break;
                }
                
                // Add patients from this batch
                allPatients = [...allPatients, ...response.patients];
                
                // Save batch to IndexedDB incrementally (but don't update UI until complete)
                if (response.patients.length > 0) {
                    console.time(`💾 Saving ${response.patients.length} patients to IndexedDB`);
                    await saveListPatients(response.patients, investigation);
                    console.timeEnd(`💾 Saving ${response.patients.length} patients to IndexedDB`);
                    
                    // Dispatch progress update for UI
                    const total = response.pagination?.total || 0;
                    const loaded = allPatients.length;
                    const percentage = total > 0 ? Math.round((loaded / total) * 100) : 0;
                    
                    dispatch({
                        type: types.FETCH_PATIENTS_PROGRESS,
                        loaded: loaded,
                        total: total,
                        percentage: percentage
                    });
                    
                    // Enhanced progress logging for large datasets
                    const progressPercent = response.pagination?.total 
                        ? Math.round((allPatients.length / response.pagination.total) * 100)
                        : '?';
                    console.log(`✅ Progress: ${allPatients.length} patients loaded${response.pagination?.total ? ` / ${response.pagination.total}` : ''} (${progressPercent}%) - Loading continues...`);
                }
                
                // Check if more data exists
                hasMore = response.pagination?.hasMore ?? false;
                offset += pageSize;
                
                // Add pause between batches to prevent overwhelming server/browser
                if (hasMore && PAGINATION_CONFIG.PAUSE_BETWEEN_BATCHES_MS > 0) {
                    await new Promise(resolve => setTimeout(resolve, PAGINATION_CONFIG.PAUSE_BETWEEN_BATCHES_MS));
                }
                
                // Safety check: prevent infinite loop
                if (offset > PAGINATION_CONFIG.MAX_TOTAL_PATIENTS) {
                    console.warn(`🛑 Pagination safety limit reached (${PAGINATION_CONFIG.MAX_TOTAL_PATIENTS} patients)`);
                    break;
                }
            }
            
            console.timeEnd('⏱️ Total patient fetch with pagination');
            console.log(`🎉 Pagination complete: ${allPatients.length} total patients loaded`);
            
            // Final update - only update timestamp when ALL data is loaded
            localStorage.setItem("lastUpdatePatients", currentCallTimestamp);
            
            // Get final list from IndexedDB and dispatch SUCCESS only once at the end
            const finalListPatients = await getAllPatientsInvestigation(investigation.uuid);
            investigation.patientsPersonalData = finalListPatients;
            
            // Save latestEventId for this investigation (only on last page) for event-based incremental sync
            if (lastResponse && lastResponse.pagination?.latestEventId !== undefined) {
                setLastSyncEventId(uuidInvestigation, lastResponse.pagination.latestEventId, finalListPatients.length);
            }
            
            // Dispatch success ONLY after all data is loaded (loading indicator will hide now)
            dispatch({
                type: types.FETCH_NEW_PATIENTS_SUCCESS,
                patients: allPatients,
                investigation: investigation
            });
            
        } catch (error) {
            console.error('❌ Error during paginated fetch:', error);
            try {
                const pagesCompleted = pageSize > 0 ? Math.floor(offset / pageSize) : undefined;
                addBreadcrumb('exception', 'patients.pagination.failed', {
                    atOffset: offset,
                    pagesCompleted,
                    pageSize,
                    returnedCount: allPatients.length,
                    total: lastResponse?.pagination?.total,
                    errorName: error?.name,
                    errorMessage: error?.message,
                    code: error?.code,
                    status: error?.response?.status,
                    memoryUsedMB: memoryMB(),
                });
                flush('pagination-failed');
            } catch (_) { /* never break reducer flow */ }

            if (!error?.status && !error?.response) {
                // Offline mode - don't update timestamp, keep old one for retry
                dispatch({
                    type: types.FETCH_NEW_PATIENTS_SUCCESS,
                    investigation:{investigation:{investigation:{patientsPersonalData:[]}}},
                    patients: [],
                });
            } else {
                // Real error - don't update timestamp. FINISH must go out before
                // the rethrow or the blocking overlay never clears.
                dispatch({ type: types.PATIENTS_DOWNLOAD_FINISH });
                dispatch({ type: types.SAVE_PATIENT_ERROR });
                throw error;
            }
        }
    };
}

// Alias for backward compatibility - now uses the same implementation as fetchPatientsAction
export const updatePatientsFromId = fetchPatientsAction;

// ============================================
// Event-Based Incremental Sync
// ============================================

/**
 * Helper function to process individual patient events
 * @param {Object} event - Patient event object (CREATED, UPDATED, or DELETED)
 * @param {Object} investigation - Investigation object with decryption keys
 */
async function processPatientEvent(event, investigation) {
    try {
        switch (event.eventType) {
            case 'CREATED':
            case 'UPDATED':
                // Backend embeds the patient payload in the event response (see
                // researcher.controller.ts → getPatientEvents). `patient === null`
                // means the patient is no longer in the investigation (orphaned
                // event from a removal that didn't record a DELETED event); drop
                // it locally. Fall back to a single-patient fetch only when the
                // field is absent (older API).
                let patient = event.patient;
                if (patient === undefined) {
                    const patientResponse = await getPatientByUuid(
                        investigation.uuid,
                        event.patientUuid
                    );
                    patient = patientResponse.patient ?? null;
                }

                if (patient) {
                    await saveListPatients([patient], investigation);
                    console.log(`✅ ${event.eventType}: ${event.patientUuid}`);
                    const stored = await fetchPatient(event.patientUuid);
                    return stored ? { op: 'upsert', patient: stored } : null;
                } else {
                    await deletePatient(event.patientUuid);
                    console.log(`🧹 Orphaned ${event.eventType} for ${event.patientUuid} — removed from local store`);
                    return { op: 'delete', uuid: event.patientUuid };
                }
                
            case 'DELETED':
                // Remove from IndexedDB
                await deletePatient(event.patientUuid);
                console.log(`🗑️ DELETED: ${event.patientUuid}`);
                return { op: 'delete', uuid: event.patientUuid };

            // SHER-723: a patient that was restored server-side. Handled exactly like
            // CREATED/UPDATED — the API hydrates the patient payload for every event type
            // except DELETED, so the record is already here and just needs writing back.
            // This case is not optional: without it the event falls through to `default`,
            // which drops it while the sync cursor still advances, so the restored patient
            // would stay invisible on this device until a full re-fetch.
            case 'RESTORED': {
                let restored = event.patient;
                if (restored === undefined) {
                    const patientResponse = await getPatientByUuid(investigation.uuid, event.patientUuid);
                    restored = patientResponse.patient ?? null;
                }
                if (!restored) {
                    console.warn(`⚠️ RESTORED with no patient payload: ${event.patientUuid}`);
                    return null;
                }
                await saveListPatients([restored], investigation);
                const storedRestored = await fetchPatient(event.patientUuid);
                console.log(`♻️ RESTORED: ${event.patientUuid}`);
                return storedRestored ? { op: 'upsert', patient: storedRestored } : null;
            }
                
            default:
                console.warn(`⚠️ Unknown event type: ${event.eventType}`);
                return null;
        }
    } catch (error) {
        console.error(`❌ Error processing event ${event.eventId ?? event.patientUuid}:`, error);
        // Don't throw - continue processing other events
        return null;
    }
}

/**
 * Incremental patient sync using event-based approach
 * Fetches only the changes since the last sync (much faster than full sync)
 * 
 * @param {string} uuidInvestigation - Investigation UUID
 * @param {boolean} silent - If true, don't show loading indicators
 * @returns {Function} Redux thunk
 */
export function incrementalSyncPatientsAction(uuidInvestigation, silent = true) {
    return async (dispatch, getState) => {
        console.log('🔄 Starting incremental patient sync (event-based)');
        
        if (!silent) {
            dispatch({ type: types.UPDATING_PATIENTS_LOADING });
        }
        
        const state = getState();
        const investigations = state.investigations;
        const investigation = investigations.data.find(
            inv => inv.uuid === uuidInvestigation
        );

        // Clear the up-front loading flag (non-silent) while keeping the local data, so
        // pages gated on patients.loading never hang on an offline/transient path. Single
        // definition reused by the offline branch and the transient-error catch below.
        const resolveWithLocalData = async () => {
            if (!silent) {
                const existing = getState().patients.data?.[uuidInvestigation]
                    ?? await getAllPatientsInvestigation(uuidInvestigation);
                investigation.patientsPersonalData = existing;
                dispatch({
                    type: types.FETCH_NEW_PATIENTS_SUCCESS,
                    patients: existing,
                    investigation: investigation
                });
            }
        };

        if (!investigation) {
            console.error('❌ Investigation not found:', uuidInvestigation);
            // No investigation to load patients from — clear the loading flag (non-silent)
            // via the error state so the page doesn't sit on a permanent loader.
            if (!silent) {
                dispatch({ type: types.SAVE_PATIENT_ERROR });
            }
            return;
        }

        // Check if offline
        if (!navigator.onLine) {
            console.log('📴 Offline mode - skipping incremental sync');
            await resolveWithLocalData();
            return;
        }
        
        try {
            // Get last synced event ID for this investigation (per-investigation sync state)
            let sinceEventId = getLastSyncEventId(uuidInvestigation);
            if (sinceEventId === null) {
                console.log('⚠️ No lastEventId for this investigation, falling back to full sync');
                return dispatch(fetchPatientsAction(uuidInvestigation, silent));
            }
            
            console.log(`📊 Last synced event ID [${uuidInvestigation}]: ${sinceEventId}`);
            
            let totalEventsProcessed = 0;
            const syncPatches = [];
            const EVENT_PAGE_LIMIT = 500; // Backend default, max 1000
            
            // Fetch and process all event pages (backend may return hasMore for large catch-up)
            while (true) {
                const response = await getPatientEvents(uuidInvestigation, sinceEventId, EVENT_PAGE_LIMIT);
                const events = response.events || [];
                
                for (const event of events) {
                    const patch = await processPatientEvent(event, investigation);
                    if (patch) syncPatches.push(patch);
                    sinceEventId = event.eventId;
                    totalEventsProcessed += 1;
                }
                
                // Persist nextEventId so we can resume if interrupted
                const nextEventId = response.pagination?.nextEventId != null
                    ? response.pagination.nextEventId
                    : (events.length > 0 ? events[events.length - 1].eventId : sinceEventId);
                setLastSyncEventId(uuidInvestigation, nextEventId);
                
                if (!response.pagination?.hasMore) break;
                sinceEventId = nextEventId;
            }
            
            if (totalEventsProcessed === 0) {
                console.log('✅ No new events - database is up to date');
            } else {
                console.log(`✅ Incremental sync complete: ${totalEventsProcessed} events processed, eventId now at ${sinceEventId}`);
            }

            const existingPatients = getState().patients.data?.[uuidInvestigation] || [];
            const updatedPatients = totalEventsProcessed > 0
                ? applyPatientSyncPatches(existingPatients, syncPatches)
                : existingPatients;

            setLastSyncEventId(uuidInvestigation, sinceEventId, updatedPatients.length);

            if (!silent || totalEventsProcessed > 0) {
                investigation.patientsPersonalData = updatedPatients;
                dispatch({
                    type: types.FETCH_NEW_PATIENTS_SUCCESS,
                    patients: updatedPatients,
                    investigation: investigation
                });
            }
            
        } catch (error) {
            console.error('❌ Incremental sync failed:', error);
            
            // Transient failures — auth-expired (401), server (5xx) or network — keep the
            // local data and clear the loading flag (like the offline branch), so pages
            // gated on patients.loading don't show a permanent loader. A 401 also triggers
            // logout elsewhere (the axios interceptor); here we only avoid the stuck loader.
            if (error.status === 401 || error.status >= 500 || !error.response) {
                console.error('⚠️ Transient sync error (auth/server/network) — keeping local data, will retry next cycle');
                await resolveWithLocalData();
                return;
            }

            // For other errors, surface an error state (also clears the loading flag).
            if (!silent) {
                dispatch({ type: types.SAVE_PATIENT_ERROR });
            }
        }
    };
}

export function updatePatientAction(investigation, uuidPatient, patientData) {
    return async (dispatch, getState) => {
      dispatch({ type: types.SAVE_PATIENT_LOADING });
  
      return updatePersonalDataPatientService(investigation.uuid, uuidPatient, patientData)
        .then(async (response) => {
            await saveListPatients([response.patient], investigation);
            const stored = await fetchPatient(response.patient.uuid);
            const currentList = getState().patients.data?.[investigation.uuid] || [];
            const updatedListPatients = upsertPatientInList(currentList, stored);
            investigation.patientsPersonalData = updatedListPatients;
            dispatch({
                type: types.UPDATE_PATIENT_SUCCESS,
                patient: {...response.patient},
                uuidPatient:uuidPatient,
                investigation:investigation
            });
        })
        .catch((error) => {
          if(!error.status && !error.response){
            const offlinePost = patientData;  
            offlinePost.uuid = uuidPatient;
            dispatch({
                type: types.UPDATE_PATIENT_OFFLINE,
                patient: {...offlinePost},
                uuidPatient:uuidPatient,
                investigation:investigation
            });
          }
          else{
            dispatch({ type: types.SAVE_PATIENT_ERROR });
            throw error;
          }
          
          
        });
    };
  }

  export function updatePatientInsuranceAction(uuidInvestigation, uuidPatient, newInsuranceId) {
    return async (dispatch) => {
      // Update the database
      await updatePatientPersonalData(uuidPatient, {coverage: newInsuranceId });
      
      // Update Redux state
      dispatch({
        type: types.UPDATE_PATIENT_INSURANCE_SUCCESS,
        uuidInvestigation,
        uuidPatient,
        newInsuranceId
      });
    };
  }

  // Persist the last patient search so its results can be restored when the user
  // navigates back from a patient detail to the search screen.
  export function setPatientSearchAction(search) {
    return { type: types.SET_PATIENT_SEARCH, search };
  }

  export function clearPatientSearchAction() {
    return { type: types.CLEAR_PATIENT_SEARCH };
  }

